import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import 'react-quill/dist/quill.snow.css';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
} from '@mui/material';
import { MURAL_VAZIO } from '../../../components/UI/EditorTexto/editorTexto.utils';
import { salvarInformacoesMuralService } from '../../../services';
import { EditorTexto } from '../../../components/AtualizarMuralDashboard/atualizarMuralDashboard.styles';
import Editor from '../../../components/UI/EditorTexto';

/**
 * Dialogo de edicao do Mural de Avisos, acionado pelo icone de editar do
 * `MuralAvisosCard`. Reaproveita o mesmo endpoint real do
 * `AtualizarMuralDashboard` de producao (`salvarInformacoesMuralService`,
 * POST /core/dashboard/mural) — nenhuma logica de negocio nova, so a camada
 * visual em MUI v5 + ReactQuill direto (nao o wrapper MUI v4 `Editor`, para
 * nao misturar MUI v4/v5 na mesma arvore). Ver
 * docs/plano-mural-avisos-card-botao-edicao.md.
 *
 * Sanitizacao do HTML do mural e responsabilidade do backend/ponto de
 * gravacao, nao deste dialogo — mesmo padrao ja aceito em producao e no
 * `MuralAvisosCard` (leitura via dangerouslySetInnerHTML).
 */
export default function EditarMuralDialog({
  open, valorAtual, onClose, onSalvo,
}) {
  const [valor, setValor] = useState(valorAtual || '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(false);
  const montadoRef = useRef(true);

  useEffect(() => {
    montadoRef.current = true;
    return () => { montadoRef.current = false; };
  }, []);

  useEffect(() => {
    if (open) {
      setValor(valorAtual || '');
      setErro(false);
    }
  }, [open, valorAtual]);

  const vazio = !valor || valor === MURAL_VAZIO;

  async function onClickPublicar() {
    setSalvando(true);
    setErro(false);
    try {
      await salvarInformacoesMuralService({ informacaoMural: valor });
      if (!montadoRef.current) return;
      onSalvo(valor);
    } catch (e) {
      if (montadoRef.current) setErro(true);
    } finally {
      if (montadoRef.current) setSalvando(false);
    }
  }

  return (
    <Dialog open={open} onClose={salvando ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Editar Mural de Avisos</DialogTitle>
      <DialogContent>


        <EditorTexto>
          <Editor
            isMural
            isAllModules
            name="mural"
            label="Editar"
            value={valor}
            readOnly={salvando}
            placeholder="Digite um novo Mural"
            onChange={setValor}
          />
        </EditorTexto>


        {erro && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Não foi possível salvar o mural de avisos. Tente novamente.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setValor('')} disabled={vazio || salvando} color="inherit">
          Limpar
        </Button>
        <Button onClick={onClose} disabled={salvando}>Cancelar</Button>
        <Button onClick={onClickPublicar} variant="contained" disabled={salvando}>
          Publicar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditarMuralDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  valorAtual: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSalvo: PropTypes.func.isRequired,
};

EditarMuralDialog.defaultProps = {
  valorAtual: '',
};
